import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, ListChecks, LogOut, Home, Receipt, MessageSquare, CalendarClock, Gift, Globe, Menu, X, User } from "lucide-react";
import { LOGO } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { NotificationBell } from "@/components/NotificationBell";
import { Seo } from "@/components/Seo";

const LABELS = {
  fr: { space: "Espace Client", dashboard: "Tableau de bord", suivi: "Suivi du dossier", documents: "Mes documents",
    factures: "Factures & Contrats", messagerie: "Messagerie", rendezvous: "Rendez-vous", parrainage: "Parrainage",
    profil: "Mon profil", backSite: "Retour au site", connected: "Connecté", logout: "Déconnexion", menu: "Menu" },
  en: { space: "Client Area", dashboard: "Dashboard", suivi: "File tracking", documents: "My documents",
    factures: "Invoices & Contracts", messagerie: "Messages", rendezvous: "Appointments", parrainage: "Referral",
    profil: "My profile", backSite: "Back to site", connected: "Logged in", logout: "Log out", menu: "Menu" },
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { lang, toggle } = useLang();
  const navigate = useNavigate();
  const L = LABELS[lang];
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = [
    { to: "/dashboard", label: L.dashboard, icon: LayoutDashboard, end: true },
    { to: "/dashboard/suivi", label: L.suivi, icon: ListChecks },
    { to: "/dashboard/documents", label: L.documents, icon: FileText },
    { to: "/dashboard/factures", label: L.factures, icon: Receipt },
    { to: "/dashboard/messagerie", label: L.messagerie, icon: MessageSquare },
    { to: "/dashboard/rendez-vous", label: L.rendezvous, icon: CalendarClock },
    { to: "/dashboard/parrainage", label: L.parrainage, icon: Gift },
    { to: "/dashboard/profil", label: L.profil, icon: User },
  ];

  const NavItems = ({ onNavigate }) => (
    <>
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} data-testid={`dash-nav-${it.to.split("/").pop() || "home"}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? "bg-gold text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
          <it.icon size={18} /> {it.label}
        </NavLink>
      ))}
      <button onClick={toggle} data-testid="dash-lang-toggle" className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors w-full">
        <Globe size={18} /> {lang === "fr" ? "English" : "Français"}
      </button>
      <NavLink to="/" onClick={onNavigate} className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors">
        <Home size={18} /> {L.backSite}
      </NavLink>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F3F4F6]" data-testid="dashboard-layout">
      <Seo title="Espace client | Work & Travel Expert" description="Votre tableau de bord Work & Travel Expert." path="/dashboard" noindex />
      <aside className="hidden md:flex flex-col w-64 bg-navy text-white shrink-0">
        <div className="p-6 flex items-center justify-between gap-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-full h-11 w-11 flex items-center justify-center p-0.5">
              <img src={LOGO} alt="Logo" className="h-full w-full object-contain rounded-full" />
            </div>
            <span className="font-heading text-lg">{L.space}</span>
          </div>
          <NotificationBell testId="notif-bell-desktop" />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavItems />
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="px-4 mb-3 text-sm">
            <div className="text-white/50 text-xs">{L.connected}</div>
            <div className="truncate">{user?.name}</div>
          </div>
          <button data-testid="dash-logout" onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-gold transition-colors w-full">
            <LogOut size={18} /> {L.logout}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden bg-navy text-white flex items-center justify-between px-5 py-4">
          <button onClick={() => setMobileOpen(true)} data-testid="dash-mobile-menu-btn" className="flex items-center gap-2 text-white">
            <Menu size={22} /> <span className="font-heading text-lg">{L.space}</span>
          </button>
          <div className="flex items-center gap-4">
            <NotificationBell testId="notif-bell-mobile" />
            <button onClick={() => { logout(); navigate("/"); }} className="text-white/80"><LogOut size={20} /></button>
          </div>
        </header>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50" data-testid="dash-mobile-drawer">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[80%] bg-navy text-white flex flex-col shadow-xl">
              <div className="p-5 flex items-center justify-between gap-3 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full h-10 w-10 flex items-center justify-center p-0.5">
                    <img src={LOGO} alt="Logo" className="h-full w-full object-contain rounded-full" />
                  </div>
                  <span className="font-heading text-lg">{L.space}</span>
                </div>
                <button onClick={() => setMobileOpen(false)} data-testid="dash-mobile-close" className="text-white/80"><X size={22} /></button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                <NavItems onNavigate={() => setMobileOpen(false)} />
              </nav>
              <div className="p-4 border-t border-white/10">
                <div className="px-4 mb-3 text-sm">
                  <div className="text-white/50 text-xs">{L.connected}</div>
                  <div className="truncate">{user?.name}</div>
                </div>
                <button onClick={() => { logout(); navigate("/"); }}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-gold transition-colors w-full">
                  <LogOut size={18} /> {L.logout}
                </button>
              </div>
            </aside>
          </div>
        )}

        <div className="p-5 sm:p-10 max-w-5xl mx-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
